import {useLingui} from '@lingui/react/macro';
import isEqual from 'react-fast-compare';
import {Svg, Path} from 'react-native-svg';
import {useCallback, useState, useEffect, createRef, useRef, memo} from 'react';
import {useColorScheme, StyleSheet, Pressable, View} from 'react-native';
import {useNavigate} from 'react-router';
import {useCopyLink} from 'interface/hooks/useCopyLink';
import {useMenuFiles} from 'interface/hooks/useMenuFiles';
import {useModalFlags} from 'interface/hooks/useModalFlags';
import {useModalShare} from 'interface/hooks/useModalShare';
import {useModalPassword} from 'interface/hooks/useModalPassword';
import {useModalFileDrop} from 'interface/hooks/useModalFileDrop';
import {useModalUpsellFeature} from 'interface/hooks/useModalUpsellFeature';
import {useDragDropItem} from 'interface/hooks/useDragDropItem';
import {usePopup} from 'extensions/viewport/usePopup';
import {IconButton} from 'interface/base/IconButton';
import {Mouse} from 'interface/base/Mouse';
import {DropFrame} from 'interface/base/DropFrame';
import {FileIcon} from 'interface/stacks/FileIcon';
import {FileImage} from 'interface/stacks/FileImage';
import {FileListItemRow} from 'interface/stacks/FileListItemRow';
import {FileListItemCell} from 'interface/stacks/FileListItemCell';
import {FilesItemFlag} from 'store/slices/files/types';
import {getPhoto} from 'utils/mediafire';
import {isTouch} from 'utils/platform';
import {testBit} from 'utils/common/data';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';
import device from 'extensions/device';

import type {MouseEvent} from 'react';
import type {GestureResponderEvent, PressableStateCallbackType} from 'react-native';
import type {FilesItem} from 'store/slices/files/types';

export interface FileListItemProps {
  item: FilesItem,
  layout: FileListItemLayout,
  focused?: boolean,
  dragItems?: string[],
  selectItems?: string[],
  linkParent?: boolean,
  isPremium?: boolean,
  hasPrivate?: boolean,
  disableDrag?: boolean,
  disableOpen?: boolean,
  disableFocus?: boolean,
  disableSelect?: boolean,
  hideExtraIcons?: boolean,
  hideIcons?: boolean,
  rootFolder?: string,
  onOpen(item: FilesItem): void,
  onFocus(item: FilesItem): void,
  onSelect(item: FilesItem, e?: GestureResponderEvent): void,
}

export enum FileListItemLayout {
  ListMinimal,
  ListNormal,
  GridSimple,
  GridSmall,
  GridMedium,
  GridLarge,
}

export const FileListItem = memo((props: FileListItemProps) => {
  const {t} = useLingui();
  const dndRef = useRef<View>(null);
  const scheme = useColorScheme();
  const [hasImageError, setHasImageError] = useState(false);
  const isRow = props.layout <= FileListItemLayout.ListNormal;
  const isCell = props.layout >= FileListItemLayout.GridSimple;
  const isSmallGrid = props.layout === FileListItemLayout.GridSmall || props.layout === FileListItemLayout.GridSimple;
  const isMediumGrid = props.layout === FileListItemLayout.GridMedium;
  const isLargeGrid = props.layout === FileListItemLayout.GridLarge;
  const isOwned = testBit(props.item.flag, FilesItemFlag.isOwned);
  const isInfected = testBit(props.item.flag, FilesItemFlag.isInfected);
  const isReported = testBit(props.item.flag, FilesItemFlag.isReported);
  const isCopyright = testBit(props.item.flag, FilesItemFlag.isCopyright);
  const isRestricted = testBit(props.item.flag, FilesItemFlag.isRestricted);
  const isWordfilter = testBit(props.item.flag, FilesItemFlag.isWordFiltered);
  const isFlagged = isReported || isCopyright || isRestricted || isWordfilter || isInfected;
  const isDeleted = !!props.item.deleted;
  const isPublic = !props.item.private && props.item.shareLinkEnabled !== false && !isDeleted;
  const isFileDrop = props.item.filedrop && isOwned;
  const isProtected = props.item.password && isOwned;
  const isForeign = props.linkParent && !isDeleted;
  const isDragging = props.dragItems && props.dragItems?.indexOf(props.item.id) !== -1;
  const isSelected = props.selectItems && props.selectItems?.indexOf(props.item.id) !== -1;
  const isMultiSelect = props.selectItems && props.selectItems?.length > 0;
  const {isDropping} = useDragDropItem({
    ref: dndRef,
    target: props.item,
    selection: props.selectItems || [props.item.id],
  });

  // File flagging (copyright, DMCA, virus, etc.)
  let textFlagged = '';
  if (isFlagged) textFlagged = 'This file has been restricted from sharing';
  if (isCopyright) textFlagged += ' due to copyright claim.';
  else if (isReported) textFlagged += ' due to DMCA takedown.';
  else if (isInfected) textFlagged += ' due to a virus detected.';

  // Dynamic styles
  const hoverColor = scheme === 'dark'
    ? 'rgba(255, 255, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.03)';

  const classes = {
    root: (e: PressableStateCallbackType) => [
      styles.root,
      isRow && styles.row,
      isCell && styles.cell,
      // props.focused && styles.focused,
      isSelected && !isDragging && styles.selected,
      isDragging && styles.dragging,
      (e.hovered
        || (e.focused && !props.disableFocus)
        || (e.pressed && !props.disableOpen)
      ) && !isSelected && {backgroundColor: hoverColor},
    ],
  };
  
  // Handlers
  const open = useCallback(() => props?.onOpen(props.item), [props.item, props.onOpen]);
  const focus = useCallback(() => props?.onFocus(props.item), [props.item, props.onFocus]);
  const select = useCallback((e?: GestureResponderEvent) => props?.onSelect(props.item, e), [props.item, props.onSelect]);
  const gotoParent = useCallback(() => navigate(`/folder/${props.item.parentKey || 'myfiles'}`), [props.item.parentKey]);
  const navigate = useNavigate();
  const copyLink = useCopyLink([props.item.id], props.item.url);
  const [showUpsellFileDrop] = useModalUpsellFeature('pro', 'filedrop');
  const [showUpsellPassword] = useModalUpsellFeature('pro', 'password');
  const popup = usePopup();
  const modalFlags = useModalFlags(textFlagged);
  const [showShare] = useModalShare([props.item.id], props.item.name, props.item.url);
  const modalPassword = useModalPassword(props.item.id, props.item.name, props.item.type === 'folder');
  const modalFileDrop = useModalFileDrop(props.item.id, props.item.name, props.item.filedrop, colors.neutral.mid);
  const menuAnchorRef = createRef<any>();
  const menuFiles = useMenuFiles(
    props.selectItems?.length > 1
      ? [...props.selectItems]
      : [props.item.id],
    props.item,
    {
      primaryColor: colors.neutral.mid,
      isPremium: props.isPremium,
      hasPrivate: props.hasPrivate,
    },
  );

  const menu = useCallback((e: MouseEvent | GestureResponderEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (popup) {
      if (e?.nativeEvent)
        popup.setPosition({x: e.nativeEvent.pageX, y: e.nativeEvent.pageY});
      popup.setRef(menuAnchorRef);
    }
    menuFiles.open();
  }, [props.item, menuFiles]);

  const longPress = useCallback((e) => {
    if (!isTouch()) return open();
    device.triggerHapticFeedback('impactLight');
    menu(e);
  }, [menu, open]);

  const renderThumbnail = useCallback(() => {
    // Only apply grid-specific styles to grid views, not list views
    const thumbnailStyle = isRow ? undefined : 
      isSmallGrid ? styles.small : 
      isMediumGrid ? styles.medium : 
      isLargeGrid ? styles.large : 
      undefined;
    
    // Determine thumbnail size based on layout
    // For list views, don't pass a size to use default dimensions
    const thumbnailSize: 'small' | 'medium' | 'large' | undefined = 
      isRow ? undefined :  // List views use default size
      isSmallGrid ? 'small' : 
      isLargeGrid ? 'large' : 
      'medium';
    
    return (
      <View style={[thumbnailStyle]}>
        {Boolean(props.item.type !== 'image' || hasImageError || !getPhoto(props.item)) &&
          <FileIcon
            size={thumbnailSize}
            id={props.item.id}
            name={props.item.name}
            type={props.item.type}
            iconColor={isSelected
              ? theme.files.listItem.selectedIcon
              : props.item.type === 'folder'
                ? colors.primary.brand
                : theme.files.icon
            }
          />
        }
        {Boolean(props.item.type === 'image' && getPhoto(props.item)) &&
          <FileImage
            size={thumbnailSize}
            source={getPhoto(props.item, thumbnailSize || 'medium')}
            onError={() => {
              setHasImageError(true);
            }}
          />
        }
      </View>
    );
  }, [hasImageError, isCell, isSmallGrid, isMediumGrid, isLargeGrid, isRow, props.item, isSelected]);

  const renderIcons = useCallback(() => (
    <View style={styles.icons} ref={menuAnchorRef}>
      {isFlagged &&
        <IconButton
          onPress={modalFlags.open}
          title={t`This file is flagged`}
          buttonStyle={styles.icon}
          icon={
            <Svg viewBox={'0 0 20 20'} width={14} height={14}>
              <Path d={vectors.icons.warning} fill={colors.secondary.redOrange}/>
            </Svg>
          }
          iconHover={
            <Svg viewBox={'0 0 20 20'} width={14} height={14}>
              <Path d={vectors.icons.warning} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
            </Svg>
          }
        />
      }
      {isFileDrop &&
        <IconButton
          onPress={props.isPremium ? modalFileDrop.open : showUpsellFileDrop}
          title={t`This folder is a FileDrop`}
          buttonStyle={styles.icon}
          icon={
            <Svg viewBox={'0 0 24 14'} width={14} height={14}>
              <Path d={vectors.icons.filedrop} fill={colors.neutral.mid}/>
            </Svg>
          }
          iconHover={
            <Svg viewBox={'0 0 24 14'} width={14} height={14}>
              <Path d={vectors.icons.filedrop} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
            </Svg>
          }
        />
      }
      {isProtected &&
        <IconButton
          onPress={props.isPremium ? modalPassword.open : showUpsellPassword}
          title={t`This item is password protected`}
          buttonStyle={styles.icon}
          icon={
            <Svg viewBox={'0 0 20 24'} width={16} height={16}>
              <Path d={vectors.icons.password} fill={colors.neutral.mid}/>
            </Svg>
          }
          iconHover={
            <Svg viewBox={'0 0 20 24'} width={16} height={16}>
              <Path d={vectors.icons.password} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
            </Svg>
          }
        />
      }
      {isPublic &&
        <IconButton
          onPress={copyLink}
          title={t`Copy share link`}
          buttonStyle={styles.icon}
          icon={
            <Svg viewBox={'0 0 20 8'} width={14} height={12}>
              <Path d={vectors.icons.public} fill={colors.neutral.mid}/>
            </Svg>
          }
          iconHover={
            <Svg viewBox={'0 0 20 8'} width={14} height={12}>
              <Path d={vectors.icons.public} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
            </Svg>
          }
        />
      }
      {isForeign &&
        <IconButton
          onPress={gotoParent}
          title={t`Go to parent`}
          buttonStyle={styles.icon}
          icon={
            <Svg viewBox={'0 0 24 18'} width={18} height={18}>
              <Path d={vectors.icons.folder} fill={colors.neutral.mid}/>
            </Svg>
          }
          iconHover={
            <Svg viewBox={'0 0 24 18'} width={18} height={18}>
              <Path d={vectors.icons.folder} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
            </Svg>
          }
        />
      }
    </View>
  ), [props.item]);
  
  useEffect(() => setHasImageError(false), [props.item.id]);

  return (
    <Pressable
      ref={props.disableDrag ? null : dndRef}
      style={classes.root}
      onFocus={props.disableFocus ? undefined : focus}
      onPress={isMultiSelect || !isTouch() ? select : props.disableOpen ? undefined : open}
      onLongPress={props.disableOpen ? undefined : longPress}
      android_ripple={{
        color: scheme === 'dark'
          ? 'rgba(255, 255, 255, 0.10)'
          : 'rgba(0, 0, 0, 0.10)'
      }}>
      <Mouse
        onDoubleClick={open}
        onContextMenu={menu}>
        {isRow
          ? <FileListItemRow
              {...props}
              menuAnchor={menuFiles.reference}
              renderIcons={renderIcons}
              renderThumbnail={renderThumbnail}
              onSelectThumbnail={select}
              onTitlePress={!isTouch() ? open : undefined}
              onShare={showShare}
              onCopyLink={copyLink}
              onDropDownMenu={menu}
            />
          : <FileListItemCell
              {...props}
              renderIcons={renderIcons}
              renderThumbnail={renderThumbnail}
              onDropDownMenu={menu}
            />
        }
      </Mouse>
      {isDropping && <DropFrame stroke="small"/>}
    </Pressable>
  );
}, (prev, next) => isEqual(prev, next));

const styles = StyleSheet.create({
  // Global
  root: {
    flex: 1,
    // @ts-ignore
    cursor: 'default',
  },
  icons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    minWidth: 42,
    height: 42,
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Layouts
  row: {
    flexDirection: 'row',
    borderColor: theme.files.listItem.border,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  cell: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    margin: 3,
  },
  // States
  focused: {
    backgroundColor: theme.files.listItem.focused,
  },
  selected: {
    borderColor: theme.files.listItem.selected,
    backgroundColor: theme.files.listItem.selected,
  },
  dragging: {
    opacity: 0.6,
    borderColor: theme.files.listItem.border,
    backgroundColor: theme.files.listItem.dragged,
  },
  // Thumbnail sizes
  small: {
    width: 60,
    height: 60,
  },
  medium: {
    width: 100,
    height: 100,
  },
  large: {
    width: 190,
    height: 190,
  },
});
